import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, AlertTriangle } from "lucide-react";
import {
  validatePAN, validateGSTIN, validateMobile, validateEmail, validateAll,
} from "@/lib/validators";

const VALUATION_METHODS = [
  { value: "FIFO", label: "FIFO (First-In-First-Out) — default" },
  { value: "WEIGHTED_AVERAGE", label: "Weighted Average" },
  { value: "SPECIFIC_IDENTIFICATION", label: "Specific Identification (lot picker)" },
  { value: "LIFO", label: "LIFO (Last-In-First-Out)" },
];

const INVOICE_TEMPLATES = [
  { value: "modern",  label: "Modern (blue accent) — default" },
  { value: "classic", label: "Classic (mono-serif, single-rule)" },
  { value: "compact", label: "Compact (tight, saves paper)" },
];

const empty = {
  name: "", address: "", state: "", gstin: "", pan: "", email: "", phone: "",
  fy_start: "2024-04-01", fy_end: "2025-03-31", currency: "INR",
  inventory_valuation_method: "FIFO",
  // Invoicing settings — printed on every Tax Invoice (Rule 46 compliance).
  signatory_name: "", signatory_designation: "Authorised Signatory",
  default_upi_vpa: "", default_terms: "",
  invoice_template: "modern", logo_url: "",
};

export default function Companies() {
  const { companies, refresh, setActive, activeId } = useCompany();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [originalMethod, setOriginalMethod] = useState("FIFO");
  const [voucherCount, setVoucherCount] = useState(0);

  useEffect(() => { refresh(); }, [refresh]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startCreate = () => {
    setForm(empty);
    setEditingId(null);
    setOriginalMethod("FIFO");
    setVoucherCount(0);
    setOpen(true);
  };

  const startEdit = async (c) => {
    setForm({
      name: c.name || "",
      address: c.address || "",
      state: c.state || "",
      gstin: c.gstin || "",
      pan: c.pan || "",
      email: c.email || "",
      phone: c.phone || "",
      fy_start: c.fy_start || "2024-04-01",
      fy_end: c.fy_end || "2025-03-31",
      currency: c.currency || "INR",
      inventory_valuation_method: c.inventory_valuation_method || "FIFO",
      signatory_name: c.signatory_name || "",
      signatory_designation: c.signatory_designation || "Authorised Signatory",
      default_upi_vpa: c.default_upi_vpa || "",
      default_terms: c.default_terms || "",
      invoice_template: c.invoice_template || "modern",
      logo_url: c.logo_url || "",
    });
    setOriginalMethod(c.inventory_valuation_method || "FIFO");
    setEditingId(c.id);
    setOpen(true);
    // Count inventory vouchers so we can decide whether to show the
    // "changing this will re-value your books" warning.
    try {
      const { data } = await api.get(`/companies/${c.id}/vouchers`);
      const inv = (data || []).filter(v =>
        ["Sales", "Purchase", "Credit Note", "Debit Note"].includes(v.voucher_type),
      );
      setVoucherCount(inv.length);
    } catch {
      setVoucherCount(0);
    }
  };

  const methodChanged = editingId && form.inventory_valuation_method !== originalMethod;
  const willRevalue = methodChanged && voucherCount > 0;

  const save = async () => {
    // Client-side validation first so the user sees the failure inline
    // before we round-trip to the server.
    const check = validateAll([
      ["GSTIN", validateGSTIN(form.gstin)],
      ["PAN",   validatePAN(form.pan)],
      ["Email", validateEmail(form.email)],
      ["Phone", validateMobile(form.phone)],
    ]);
    if (!check.ok) { toast.error(check.msg); return; }

    // Extra confirmation when re-valuing existing books.
    if (willRevalue) {
      const ok = window.confirm(
        `Switching valuation method from ${originalMethod} to ${form.inventory_valuation_method} will ` +
        `re-value ALL ${voucherCount} existing inventory voucher(s). Closing stock, ` +
        `Cost of Goods Sold, and every downstream report (P&L / Balance Sheet / Cash Flow) will change. ` +
        `\n\nAre you sure you want to continue?`
      );
      if (!ok) return;
    }

    setSaving(true);
    try {
      let data;
      if (editingId) {
        ({ data } = await api.put(`/companies/${editingId}`, form));
        toast.success(`Company "${data.name}" updated`);
      } else {
        ({ data } = await api.post("/companies", form));
        toast.success(`Company "${data.name}" created`);
        setActive(data.id);
      }
      if (willRevalue) {
        try {
          const { data: rep } = await api.post(`/companies/${editingId}/recompute-valuation`);
          const delta = Number(rep.delta || 0).toFixed(2);
          const sign = rep.delta >= 0 ? "+" : "";
          toast.success(
            `Re-valued ${rep.vouchers_scanned} vouchers under ${rep.method}. ` +
            `Closing stock change: ₹${sign}${delta}`,
            { duration: 8000 }
          );
        } catch (e) {
          toast.error("Re-valuation failed: " + formatApiError(e.response?.data?.detail));
        }
      }
      setForm(empty);
      setEditingId(null);
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"? This deletes ALL its ledgers and vouchers.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ── Logo upload / delete (Rule 46 Ship 3) ────────────────────────────
  const [logoBusy, setLogoBusy] = useState(false);
  const uploadLogo = async (file) => {
    if (!editingId) {
      toast.error("Save the company first, then upload a logo.");
      return;
    }
    if (!/\.(png|jpe?g|webp)$/i.test(file.name || "")) {
      toast.error("Logo must be PNG, JPG or WebP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo is over 2 MB.");
      return;
    }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/companies/${editingId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setLogoBusy(false);
    }
  };
  const clearLogo = async () => {
    if (!editingId) { setForm((f) => ({ ...f, logo_url: "" })); return; }
    if (!window.confirm("Remove logo from this company?")) return;
    setLogoBusy(true);
    try {
      await api.delete(`/companies/${editingId}/logo`);
      setForm((f) => ({ ...f, logo_url: "" }));
      toast.success("Logo removed");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="Create, edit and manage multiple companies"
        actions={
          <Dialog
            open={open}
            onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); } }}
          >
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-new-company">
                <Plus className="w-4 h-4 mr-1" /> New Company
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl w-[96vw] max-h-[94vh] overflow-hidden flex flex-col p-0">
              <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100 bg-white">
                <DialogTitle className="text-lg">{editingId ? "Edit company" : "Create new company"}</DialogTitle>
                <p className="text-xs text-gray-500 mt-1">
                  Fields marked * are required. Others can be added or edited later.
                </p>
              </DialogHeader>
              <div className="overflow-y-auto flex-1" data-testid="company-dialog-body">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-5">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Company name <span className="text-red-500">*</span></Label>
                  <Input value={form.name} onChange={onChange("name")} placeholder="e.g. Acme Trading Co Pvt Ltd" className="h-10" data-testid="input-company-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">GSTIN</Label>
                  <Input value={form.gstin} onChange={onChange("gstin")} placeholder="27ABCDE1234F1Z5" className="h-10 font-mono text-sm" data-testid="input-company-gstin" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">PAN</Label>
                  <Input value={form.pan} onChange={onChange("pan")} placeholder="ABCDE1234F" className="h-10 font-mono text-sm" data-testid="input-company-pan" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">State</Label>
                  <Input value={form.state} onChange={onChange("state")} placeholder="Maharashtra" className="h-10" data-testid="input-company-state" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Phone</Label>
                  <Input value={form.phone} onChange={onChange("phone")} placeholder="98xxxxxxxx" className="h-10" data-testid="input-company-phone" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Email</Label>
                  <Input type="email" value={form.email} onChange={onChange("email")} placeholder="accounts@company.com" className="h-10" data-testid="input-company-email" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Address</Label>
                  <Input value={form.address} onChange={onChange("address")} placeholder="Building, street, city, PIN" className="h-10" data-testid="input-company-address" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">FY Start</Label>
                  <Input type="date" value={form.fy_start} onChange={onChange("fy_start")} className="h-10" data-testid="input-company-fy-start" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">FY End</Label>
                  <Input type="date" value={form.fy_end} onChange={onChange("fy_end")} className="h-10" data-testid="input-company-fy-end" />
                </div>

                {/* Inventory Valuation Method — drives COGS, closing stock, and all inventory reports */}
                <div className="sm:col-span-2 space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">
                    Inventory valuation method
                  </Label>
                  <select
                    value={form.inventory_valuation_method}
                    onChange={onChange("inventory_valuation_method")}
                    className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    data-testid="select-valuation-method"
                  >
                    {VALUATION_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  {willRevalue ? (
                    <div
                      className="mt-2 p-3 border-2 border-red-400 bg-red-50 rounded flex items-start gap-2"
                      data-testid="revalue-warning-banner"
                    >
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-red-900 leading-relaxed">
                        <div className="font-semibold text-red-800 uppercase tracking-wider mb-1">
                          Warning — this will re-value your books
                        </div>
                        Changing from <b>{originalMethod}</b> to <b>{form.inventory_valuation_method}</b> will
                        recompute closing stock and COGS for all {voucherCount} inventory voucher(s).
                        Your P&amp;L, Balance Sheet and Cash Flow figures will change. Auditors expect a
                        journal note explaining the switch.
                      </div>
                    </div>
                  ) : editingId ? (
                    <p className="text-xs text-gray-500 mt-1">
                      No inventory vouchers yet — safe to change.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Determines how Cost of Goods Sold and closing stock are calculated.
                      For &quot;Specific Identification&quot;, each sale asks you to pick which purchase lot to consume.
                    </p>
                  )}
                </div>
              </div>

              {/* Invoicing settings — populates printed Tax Invoice (Rule 46) */}
              <div className="px-6 py-5 border-t border-gray-100 bg-blue-50/40">
                <h4 className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-4 flex items-center gap-1.5">
                  <span className="w-1 h-4 bg-blue-700 rounded"></span>
                  Invoicing settings
                </h4>

                {/* Rule 46 Ship 3 — Logo + Template */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Company logo</Label>
                    {form.logo_url ? (
                      <div className="flex items-center gap-3 border border-gray-200 bg-white rounded p-2">
                        <img
                          src={form.logo_url.startsWith("/api") ? form.logo_url : form.logo_url}
                          alt="Logo preview"
                          className="h-12 w-auto max-w-[80px] object-contain bg-white"
                          data-testid="img-logo-preview"
                        />
                        <div className="flex-1 text-xs text-gray-600">Displayed on every printed invoice.</div>
                        <button
                          type="button"
                          onClick={clearLogo}
                          disabled={logoBusy}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                          data-testid="btn-remove-logo"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label
                        className="flex items-center justify-center border-2 border-dashed border-gray-300 bg-white rounded p-4 text-xs text-gray-500 cursor-pointer hover:border-blue-400 hover:text-blue-700"
                        data-testid="dropzone-logo"
                      >
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          disabled={logoBusy || !editingId}
                          onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                          data-testid="input-logo-file"
                        />
                        {logoBusy ? "Uploading…" : editingId ? "Click to upload PNG / JPG / WebP · ≤ 2 MB" : "Save the company first, then upload a logo."}
                      </label>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Invoice template</Label>
                    <select
                      value={form.invoice_template}
                      onChange={onChange("invoice_template")}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      data-testid="select-invoice-template"
                    >
                      {INVOICE_TEMPLATES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <div className="text-[11px] text-gray-500">
                      Visual style for printed / PDF-saved invoices. Compliance content is identical across templates.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Signatory name</Label>
                    <Input value={form.signatory_name} onChange={onChange("signatory_name")} placeholder="e.g. Ankit Marfatia" className="h-10" data-testid="input-signatory-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Designation</Label>
                    <Input value={form.signatory_designation} onChange={onChange("signatory_designation")} className="h-10" data-testid="input-signatory-designation" />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">
                      UPI VPA <span className="text-gray-400 normal-case font-normal">(printed as QR + button on every invoice)</span>
                    </Label>
                    <Input
                      value={form.default_upi_vpa}
                      onChange={onChange("default_upi_vpa")}
                      placeholder="acme@hdfcbank"
                      className="h-10"
                      data-testid="input-upi-vpa"
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-700 font-semibold">Default terms &amp; conditions</Label>
                    <textarea
                      value={form.default_terms}
                      onChange={onChange("default_terms")}
                      rows={3}
                      placeholder="Payment due within 30 days. Interest @18% on delayed payments. Subject to Mumbai jurisdiction."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                      data-testid="input-default-terms"
                    />
                  </div>
                </div>
              </div>
              </div>
              <DialogFooter className="px-6 py-3 border-t border-gray-100 bg-white sticky bottom-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  disabled={saving || !form.name}
                  onClick={save}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="btn-save-company"
                >
                  {saving ? "Saving…" : editingId ? "Save changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6">
        {companies.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded p-10 text-center">
            <Building2 className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No companies yet. Create your first company to start.</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Name</th><th>GSTIN</th><th>State</th><th>FY</th><th>Active</th><th></th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} data-testid={`row-company-${c.id}`}>
                    <td className="font-medium">{c.name}</td>
                    <td className="font-mono text-xs">{c.gstin || "—"}</td>
                    <td>{c.state || "—"}</td>
                    <td className="font-mono text-xs">{c.fy_start} → {c.fy_end}</td>
                    <td>
                      {c.id === activeId ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                      ) : (
                        <button onClick={() => setActive(c.id)} className="text-xs text-blue-700 hover:underline" data-testid={`btn-activate-${c.id}`}>
                          Set active
                        </button>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(c)}
                        className="text-gray-500 hover:text-blue-700 mr-3"
                        data-testid={`btn-edit-company-${c.id}`}
                        title="Edit company"
                      >
                        <Pencil className="w-4 h-4 inline" />
                      </button>
                      <button onClick={() => remove(c)} className="text-red-600 hover:text-red-700" data-testid={`btn-delete-company-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
